import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import Section from '../common/Section';
import { FaCheckCircle, FaUsersCog, FaLock, FaRocket, FaGlobe, FaHeadset } from 'react-icons/fa';

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 3rem 2rem;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureItem = styled(motion.div)`
  display: flex;
  gap: 1.5rem;
`;

const IconContainer = styled.div`
  flex-shrink: 0;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--primary-color);
  font-size: 1.5rem;
`;

const FeatureContent = styled.div``;

const FeatureTitle = styled.h3`
  font-size: 1.25rem;
  margin-bottom: 0.75rem;
`;

const FeatureText = styled.p`
  color: var(--text-light);
  font-size: 1rem;
  line-height: 1.6;
`;

const container = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.3
    }
  }
};

const item = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { duration: 0.5 }
  }
};

const WhyChooseUs = () => {
  const features = [
    {
      icon: <FaUsersCog />,
      title: 'US Energy Trading Compliance Expertise',
      description: 'Our team brings decades of experience in US energy trading compliance, specializing in gas markets, position limits monitoring, and CFTC regulations.'
    },
    {
      icon: <FaLock />,
      title: 'CFTC Compliance & Security',
      description: 'Built-in US energy trading compliance features ensure CFTC regulatory compliance and secure handling of sensitive gas trading data and position limits.'
    },
    {
      icon: <FaRocket />,
      title: 'Fast US Energy Trading Implementation',
      description: 'Our US energy trading compliance solutions deploy rapidly with minimal disruption to your existing gas trading workflows and regulatory systems.'
    },
    {
      icon: <FaGlobe />,
      title: 'US Energy Market Coverage',
      description: 'Support for all major US energy exchanges including NYMEX, ICE, and regional power markets with local CFTC regulatory expertise.'
    },
    {
      icon: <FaCheckCircle />,
      title: 'Proven US Energy Trading Track Record',
      description: 'Trusted by leading US energy trading firms for reliable, efficient and scalable position limits monitoring and compliance solutions.'
    },
    {
      icon: <FaHeadset />,
      title: '24/7 US Energy Trading Support',
      description: 'Round-the-clock technical support ensures your US energy trading operations and position limits monitoring continue without interruption.'
    }
  ];

  return (
    <Section
      title="Why Choose Talgo for US Energy Trading Compliance"
      subtitle="We combine deep US energy trading compliance expertise with cutting-edge technology to deliver position limits monitoring solutions that drive trading excellence"
      titleProps={{ withLine: true, spaced: true, centered: true }}
      subtitleProps={{ centered: true, muted: true, narrow: true }}
      offWhite
      id="why-choose-us"
    >
      <FeatureGrid
        as={motion.div}
        variants={container}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
      >
        {features.map((feature, index) => (
          <FeatureItem key={index} variants={item}>
            <IconContainer>{feature.icon}</IconContainer>
            <FeatureContent>
              <FeatureTitle>{feature.title}</FeatureTitle>
              <FeatureText>{feature.description}</FeatureText>
            </FeatureContent>
          </FeatureItem>
        ))}
      </FeatureGrid>
    </Section>
  );
};

export default WhyChooseUs; 